import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Plus, CheckCircle2, Settings, Package, Tag, Image as ImageIcon, Truck, FileText, Search, LayoutGrid } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { useVendor } from '../context/VendorContext';
import { Header } from '../components/ui/Header';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
export function VendorAddProductScreen() {
  const {
    goBack
  } = useNavigation();
  const {
    addProduct
  } = useVendor();
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    content: '',
    sku: '',
    price: '',
    discountPrice: '',
    costPerItem: '',
    barcode: '',
    stockStatus: 'in_stock' as 'in_stock' | 'out_of_stock' | 'backorder',
    stock: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    category: '',
    brand: '',
    isFeatured: false,
    minOrderQty: '0',
    maxOrderQty: '0',
    metaTitle: '',
    metaDescription: '',
    status: 'published' as 'published' | 'draft' | 'pending',
    videoUrl: ''
  });
  const [images, setImages] = useState<string[]>([]);
  const [variants, setVariants] = useState({
    colors: [] as string[],
    sizes: [] as string[]
  });
  const [colorInput, setColorInput] = useState('');
  const [sizeInput, setSizeInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const categories = ['Smartphones', 'Laptops', 'Tablets', 'Accessories', 'Audio', 'Cameras', 'Gaming', 'Wearables'];
  const brands = ['Samsung', 'Apple', 'Sony', 'LG', 'HP', 'Dell', 'Other'];
  const availableCollections = ['Weekly Gadget Spotlight', 'Electronics Trendsetters', 'Digital Workspace Gear', 'Cutting-Edge Tech Showcase'];
  const availableLabels = ['Hot', 'New', 'Sale'];
  // Auto-generate slug from name
  useEffect(() => {
    if (formData.name && !formData.slug) {
      setFormData(prev => ({
        ...prev,
        slug: prev.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')
      }));
    }
  }, [formData.name]);
  const handleImageUpload = () => {
    if (images.length >= 5) return;
    const placeholderImage = `https://placehold.co/400x400/e5e7eb/9ca3af?text=Product+${images.length + 1}`;
    setImages([...images, placeholderImage]);
  };
  const handleRemoveImage = (index: number) => setImages(images.filter((_, i) => i !== index));
  const handleAddVariant = (type: 'colors' | 'sizes', input: string, setInput: (v: string) => void) => {
    if (input.trim() && !variants[type].includes(input.trim())) {
      setVariants({
        ...variants,
        [type]: [...variants[type], input.trim()]
      });
      setInput('');
    }
  };
  const handleRemoveVariant = (type: 'colors' | 'sizes', item: string) => {
    setVariants({
      ...variants,
      [type]: variants[type].filter(i => i !== item)
    });
  };
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };
  const toggleArrayItem = (item: string, array: string[], setArray: (arr: string[]) => void) => {
    setArray(array.includes(item) ? array.filter(i => i !== item) : [...array, item]);
  };
  const handleSubmit = (isDraft = false) => {
    setLoading(true);
    setTimeout(() => {
      addProduct({
        name: formData.name,
        category: formData.category,
        price: parseFloat(formData.price),
        discountPrice: formData.discountPrice ? parseFloat(formData.discountPrice) : undefined,
        description: formData.description,
        images: images.length > 0 ? images : ['https://placehold.co/400x400/e5e7eb/9ca3af?text=Product'],
        stock: parseInt(formData.stock) || 0,
        variants: {
          colors: variants.colors.length > 0 ? variants.colors : undefined,
          sizes: variants.sizes.length > 0 ? variants.sizes : undefined
        },
        isActive: !isDraft && formData.status === 'published'
      });
      setLoading(false);
      setShowSuccess(true);
      setTimeout(() => goBack(), 1500);
    }, 1000);
  };
  const isFormValid = formData.name && formData.category && formData.price && formData.stock;
  const Card = ({
    title,
    icon: Icon,
    children
  }: {
    title: string;
    icon?: any;
    children: React.ReactNode;
  }) => <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 space-y-4" data-id="element-1510">
      <div className="flex items-center gap-2 mb-2" data-id="element-1511">
        {Icon && <Icon className="w-5 h-5 text-primary" data-id="element-1512" />}
        <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-1513">{title}</h3>
      </div>
      {children}
    </div>;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24" data-id="element-1514">
      <Header title="New Product" data-id="element-1515" />

      {/* Publish Settings (Top) */}
      <div className="bg-white dark:bg-gray-800 p-4 border-b border-gray-100 dark:border-gray-700 sticky top-0 z-30 flex items-center justify-between gap-3 shadow-sm" data-id="element-1516">
        <select value={formData.status} onChange={e => setFormData({
        ...formData,
        status: e.target.value as any
      })} className="flex-1 px-3 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1517">
          <option value="published" data-id="element-1518">Published</option>
          <option value="draft" data-id="element-1519">Draft</option>
          <option value="pending" data-id="element-1520">Pending Review</option>
        </select>
        <div className="flex gap-2" data-id="element-1521">
          <Button variant="outline" size="sm" onClick={() => handleSubmit(true)} disabled={loading} className="border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300" data-id="element-1522">
            Save Draft
          </Button>
          <Button size="sm" onClick={() => handleSubmit(false)} loading={loading} disabled={!isFormValid} className="bg-primary text-white" data-id="element-1523">
            Publish
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4" data-id="element-1524">
        {/* 1. Basic Information */}
        <Card title="Basic Information" icon={FileText} data-id="element-1525">
          <Input label="Product Name *" placeholder="Enter product name" value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} data-id="element-1526" />
          <div data-id="element-1527">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1528">
              Permalink *
            </label>
            <div className="flex items-center bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden" data-id="element-1529">
              <span className="px-3 text-xs text-gray-500 border-r border-gray-200 dark:border-gray-700" data-id="element-1530">
                jummall.com/products/
              </span>
              <input type="text" value={formData.slug} onChange={e => setFormData({
              ...formData,
              slug: e.target.value
            })} className="flex-1 px-3 py-3 text-sm bg-transparent focus:outline-none dark:text-white" data-id="element-1531" />
            </div>
          </div>
          <div data-id="element-1532">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1533">
              Description
            </label>
            <textarea rows={4} value={formData.description} onChange={e => setFormData({
            ...formData,
            description: e.target.value
          })} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white resize-none" placeholder="Short description..." data-id="element-1534" />
          </div>
          <div data-id="element-1535">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1536">
              Content
            </label>
            <textarea rows={6} value={formData.content} onChange={e => setFormData({
            ...formData,
            content: e.target.value
          })} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white resize-none" placeholder="Detailed product content..." data-id="element-1537" />
          </div>
        </Card>

        {/* 2. Media */}
        <Card title="Media" icon={ImageIcon} data-id="element-1538">
          <div className="grid grid-cols-3 gap-3" data-id="element-1539">
            {images.map((img, idx) => <div key={idx} className="relative aspect-square" data-id="element-1540">
                <img src={img} alt={`Upload ${idx}`} className="w-full h-full object-cover rounded-xl border border-gray-200 dark:border-gray-700" data-id="element-1541" />
                <button onClick={() => handleRemoveImage(idx)} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md" data-id="element-1542">
                  <X className="w-4 h-4" data-id="element-1543" />
                </button>
              </div>)}
            {images.length < 5 && <button onClick={handleImageUpload} className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors" data-id="element-1544">
                <Upload className="w-6 h-6 text-gray-400" data-id="element-1545" />
                <span className="text-[10px] text-gray-500" data-id="element-1546">Add Image</span>
              </button>}
          </div>
          <Input label="Video URL" placeholder="https://youtube.com/..." value={formData.videoUrl} onChange={e => setFormData({
          ...formData,
          videoUrl: e.target.value
        })} data-id="element-1547" />
        </Card>

        {/* 3. Pricing & Overview */}
        <Card title="Pricing & Overview" icon={Tag} data-id="element-1548">
          <div className="grid grid-cols-2 gap-3" data-id="element-1549">
            <Input label="SKU" placeholder="e.g. JM-2443" value={formData.sku} onChange={e => setFormData({
            ...formData,
            sku: e.target.value
          })} data-id="element-1550" />
            <Input label="Barcode (UPC/GTIN)" placeholder="Enter barcode" value={formData.barcode} onChange={e => setFormData({
            ...formData,
            barcode: e.target.value
          })} data-id="element-1551" />
            <Input label="Price (₦) *" type="number" placeholder="0" value={formData.price} onChange={e => setFormData({
            ...formData,
            price: e.target.value
          })} data-id="element-1552" />
            <Input label="Sale Price (₦)" type="number" placeholder="0" value={formData.discountPrice} onChange={e => setFormData({
            ...formData,
            discountPrice: e.target.value
          })} data-id="element-1553" />
          </div>
        </Card>

        {/* 4. Stock & Inventory */}
        <Card title="Stock & Inventory" icon={Package} data-id="element-1554">
          <div data-id="element-1555">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2" data-id="element-1556">
              Stock Status
            </label>
            <div className="flex flex-col gap-2" data-id="element-1557">
              {[{
              id: 'in_stock',
              label: 'In Stock',
              color: 'bg-green-500'
            }, {
              id: 'out_of_stock',
              label: 'Out of Stock',
              color: 'bg-red-500'
            }, {
              id: 'backorder',
              label: 'On Backorder',
              color: 'bg-yellow-500'
            }].map(status => <label key={status.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${formData.stockStatus === status.id ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700 dark:bg-gray-900'}`} data-id="element-1558">
                  <input type="radio" name="stockStatus" checked={formData.stockStatus === status.id} onChange={() => setFormData({
                ...formData,
                stockStatus: status.id as any
              })} className="text-primary focus:ring-primary" data-id="element-1559" />
                  <div className={`w-2 h-2 rounded-full ${status.color}`} data-id="element-1560" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1561">
                    {status.label}
                  </span>
                </label>)}
            </div>
          </div>
          <Input label="Stock Quantity *" type="number" placeholder="0" value={formData.stock} onChange={e => setFormData({
          ...formData,
          stock: e.target.value
        })} data-id="element-1562" />
        </Card>

        {/* 5. Shipping */}
        <Card title="Shipping" icon={Truck} data-id="element-1563">
          <div className="grid grid-cols-2 gap-3" data-id="element-1564">
            <Input label="Weight (g)" type="number" placeholder="0" value={formData.weight} onChange={e => setFormData({
            ...formData,
            weight: e.target.value
          })} data-id="element-1565" />
            <Input label="Length (cm)" type="number" placeholder="0" value={formData.length} onChange={e => setFormData({
            ...formData,
            length: e.target.value
          })} data-id="element-1566" />
            <Input label="Width (cm)" type="number" placeholder="0" value={formData.width} onChange={e => setFormData({
            ...formData,
            width: e.target.value
          })} data-id="element-1567" />
            <Input label="Height (cm)" type="number" placeholder="0" value={formData.height} onChange={e => setFormData({
            ...formData,
            height: e.target.value
          })} data-id="element-1568" />
          </div>
        </Card>

        {/* 6. Categories & Organization */}
        <Card title="Organization" icon={LayoutGrid} data-id="element-1569">
          <div data-id="element-1570">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1571">
              Category *
            </label>
            <select value={formData.category} onChange={e => setFormData({
            ...formData,
            category: e.target.value
          })} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1572">
              <option value="" data-id="element-1573">Select category...</option>
              {categories.map(cat => <option key={cat} value={cat} data-id="element-1574">
                  {cat}
                </option>)}
            </select>
          </div>
          <div data-id="element-1575">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1576">
              Brand
            </label>
            <select value={formData.brand} onChange={e => setFormData({
            ...formData,
            brand: e.target.value
          })} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1577">
              <option value="" data-id="element-1578">Select brand...</option>
              {brands.map(b => <option key={b} value={b} data-id="element-1579">
                  {b}
                </option>)}
            </select>
          </div>

          <div data-id="element-1580">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2 mt-4" data-id="element-1581">
              Product Collections
            </label>
            <div className="space-y-2" data-id="element-1582">
              {availableCollections.map(col => <label key={col} className="flex items-center gap-2" data-id="element-1583">
                  <input type="checkbox" checked={collections.includes(col)} onChange={() => toggleArrayItem(col, collections, setCollections)} className="rounded text-primary focus:ring-primary border-gray-300" data-id="element-1584" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1585">
                    {col}
                  </span>
                </label>)}
            </div>
          </div>

          <div data-id="element-1586">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2 mt-4" data-id="element-1587">
              Labels
            </label>
            <div className="flex gap-4" data-id="element-1588">
              {availableLabels.map(label => <label key={label} className="flex items-center gap-2" data-id="element-1589">
                  <input type="checkbox" checked={labels.includes(label)} onChange={() => toggleArrayItem(label, labels, setLabels)} className="rounded text-primary focus:ring-primary border-gray-300" data-id="element-1590" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1591">
                    {label}
                  </span>
                </label>)}
            </div>
          </div>
        </Card>

        {/* 7. Attributes & Variants */}
        <Card title="Attributes & Variants" icon={Settings} data-id="element-1592">
          {/* Colors */}
          <div data-id="element-1593">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1594">
              Colors
            </label>
            <div className="flex gap-2 mb-2" data-id="element-1595">
              <input type="text" placeholder="e.g. Black, White" value={colorInput} onChange={e => setColorInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddVariant('colors', colorInput, setColorInput)} className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1596" />
              <button onClick={() => handleAddVariant('colors', colorInput, setColorInput)} className="w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center" data-id="element-1597">
                <Plus className="w-5 h-5" data-id="element-1598" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1599">
              {variants.colors.map(color => <span key={color} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-xs flex items-center gap-1" data-id="element-1600">
                  {color}{' '}
                  <button onClick={() => handleRemoveVariant('colors', color)} data-id="element-1601">
                    <X className="w-3 h-3" data-id="element-1602" />
                  </button>
                </span>)}
            </div>
          </div>
          {/* Sizes */}
          <div className="mt-4" data-id="element-1603">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1604">
              Sizes
            </label>
            <div className="flex gap-2 mb-2" data-id="element-1605">
              <input type="text" placeholder="e.g. S, M, L" value={sizeInput} onChange={e => setSizeInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddVariant('sizes', sizeInput, setSizeInput)} className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1606" />
              <button onClick={() => handleAddVariant('sizes', sizeInput, setSizeInput)} className="w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center" data-id="element-1607">
                <Plus className="w-5 h-5" data-id="element-1608" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1609">
              {variants.sizes.map(size => <span key={size} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-xs flex items-center gap-1" data-id="element-1610">
                  {size}{' '}
                  <button onClick={() => handleRemoveVariant('sizes', size)} data-id="element-1611">
                    <X className="w-3 h-3" data-id="element-1612" />
                  </button>
                </span>)}
            </div>
          </div>
        </Card>

        {/* 8. Product Options */}
        <Card title="Product Options" icon={Settings} data-id="element-1613">
          <label className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 cursor-pointer" data-id="element-1614">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1615">
              Is Featured?
            </span>
            <div className={`w-11 h-6 rounded-full transition-colors relative ${formData.isFeatured ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-1616">
              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${formData.isFeatured ? 'translate-x-5' : 'translate-x-0.5'}`} data-id="element-1617" />
            </div>
            <input type="checkbox" className="hidden" checked={formData.isFeatured} onChange={e => setFormData({
            ...formData,
            isFeatured: e.target.checked
          })} data-id="element-1618" />
          </label>

          <div className="grid grid-cols-2 gap-3 mt-4" data-id="element-1619">
            <Input label="Min Order Qty" type="number" placeholder="0" value={formData.minOrderQty} onChange={e => setFormData({
            ...formData,
            minOrderQty: e.target.value
          })} data-id="element-1620" />
            <Input label="Max Order Qty" type="number" placeholder="0" value={formData.maxOrderQty} onChange={e => setFormData({
            ...formData,
            maxOrderQty: e.target.value
          })} data-id="element-1621" />
          </div>

          <div className="mt-4" data-id="element-1622">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1623">
              Tags
            </label>
            <div className="flex gap-2 mb-2" data-id="element-1624">
              <input type="text" placeholder="Add tags..." value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddTag()} className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white" data-id="element-1625" />
              <button onClick={handleAddTag} className="w-10 h-10 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl flex items-center justify-center" data-id="element-1626">
                <Plus className="w-5 h-5" data-id="element-1627" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-1628">
              {tags.map(tag => <span key={tag} className="px-2.5 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg text-xs flex items-center gap-1 border border-gray-200 dark:border-gray-700" data-id="element-1629">
                  {tag}{' '}
                  <button onClick={() => setTags(tags.filter(t => t !== tag))} data-id="element-1630">
                    <X className="w-3 h-3" data-id="element-1631" />
                  </button>
                </span>)}
            </div>
          </div>
        </Card>

        {/* 9. SEO */}
        <Card title="Search Engine Optimize" icon={Search} data-id="element-1632">
          <Input label="Meta Title" placeholder="SEO Title" value={formData.metaTitle} onChange={e => setFormData({
          ...formData,
          metaTitle: e.target.value
        })} data-id="element-1633" />
          <div className="mt-3" data-id="element-1634">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1" data-id="element-1635">
              Meta Description
            </label>
            <textarea rows={2} value={formData.metaDescription} onChange={e => setFormData({
            ...formData,
            metaDescription: e.target.value
          })} className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:ring-2 focus:ring-primary dark:text-white resize-none" placeholder="SEO Description..." data-id="element-1636" />
            <p className="text-[10px] text-gray-500 mt-1" data-id="element-1637">
              Optimize meta title & description to make your site easy to
              discover on search engines.
            </p>
          </div>
        </Card>
      </div>

      {/* Success Toast */}
      {showSuccess && <motion.div initial={{
      opacity: 0,
      y: 50
    }} animate={{
      opacity: 1,
      y: 0
    }} className="fixed bottom-24 left-4 right-4 z-50 flex justify-center" data-id="element-1638">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2" data-id="element-1639">
            <CheckCircle2 className="w-5 h-5 text-success" data-id="element-1640" />
            <span className="text-sm font-medium" data-id="element-1641">
              Product saved successfully
            </span>
          </div>
        </motion.div>}
    </motion.div>;
}